const { InsertPosition, InsertPositionLabels, DistanceType, MaxInsertsPerPage } = require('../dynamicInsert/constants');
const { PLACEMENT_UI_GROUPS: UI_GROUPS } = require('./constants');
const { hideIfNot } = require('./bidParamUiUtils');

const DYNAMIC_INSERT_DEFAULT_TEMPLATE = `<div id="{{id}}"></div>`;
const DYNAMIC_INSERT_RESTRICTION_KEY = 'restriction';
const DEFAULT_TARGET_FIELD_NAMES = ['selector', 'minPixelDistance', 'distanceType'];

const PLATFORMS = {
	Mobile: 'mobile',
	Desktop: 'desktop',
	Tablet: 'tablet',
	TV: 'tv',
};

const DYNAMIC_POSITION_DEFAULTS = {
	defaultValue: InsertPosition.After,
	isRequired: true,
	hasOptions: true,
	options: Object.entries(InsertPositionLabels).map(([value, label]) => ({
		name: value,
		label,
	})),
};

const DISTANCE_TYPE_OPTIONS = {
	defaultValue: DistanceType.Percent,
	hasOptions: true,
	isRequired: true,
	options: [
		{ name: DistanceType.Pixels, label: 'Pixels' },
		{ name: DistanceType.Percent, label: 'Percent of viewport' },
	],
};

const CONTAINER_FIELDS = [
	{
		type: 'String',
		name: 'container',
		description: 'Container CSS Selector (e.g., ".article")',
		defaultValue: '',
		isRequired: false,
	},
	{
		type: 'Number',
		name: 'maxPerContainer',
		description: 'Maximum number of times the ad-slot can be inserted into the same parent element. 0 for unlimited.',
		defaultValue: null,
		isRequired: false,
		shouldHide: hideIfNot('container'),
	},
];

const DISTANCE_DESCRIPTIONS = {
	target: 'Minimum distance between ad-slots',
	restriction: 'Minimum distance away from restrictor',
};

function DYNAMIC_INSERT_DEFAULTS(type) {
	return [
		{
			type: 'Number',
			name: 'minPixelDistance',
			description: DISTANCE_DESCRIPTIONS[type] || DISTANCE_DESCRIPTIONS.target,
			defaultValue: 50,
			isRequired: false,
		},
		{
			type: 'String',
			name: 'distanceType',
			description: 'Distance unit type',
			...DISTANCE_TYPE_OPTIONS,
		},
		...(type !== DYNAMIC_INSERT_RESTRICTION_KEY ? [
			{
				type: 'String',
				name: 'position',
				description: 'Position of insert',
				...DYNAMIC_POSITION_DEFAULTS,
			}] : []),
	];
}

const DYNAMIC_INSERT_TARGET_DEFAULTS = (type) => ([
	{
		type: 'String',
		name: 'selector',
		description: `CSS Selector for ${type} element (e.g., ".article > p:nth-of-type(3n)")`,
		defaultValue: '*',
		isRequired: true,
	},
	...DYNAMIC_INSERT_DEFAULTS(type),
	...(type !== DYNAMIC_INSERT_RESTRICTION_KEY ? CONTAINER_FIELDS : []),
]);

const FieldSettings = 	{
	type: 'Object',
	uiGroup: UI_GROUPS.BUILT_IN,
	name: 'rlvDynamicInsert',
	description: 'Dynamic Placement Insertion settings',
	defaultValue: {},
	fields: [
		{
			type: 'Boolean',
			name: 'enabled',
			description: 'Enable Dynamic Placement Insertion',
			defaultValue: false,
		},
		...DYNAMIC_INSERT_TARGET_DEFAULTS('target')
			.filter((f) => DEFAULT_TARGET_FIELD_NAMES.includes(f.name))
			.map((f) => ({ ...f, isRequired: false })),
		{
			type: 'Number',
			name: 'priority',
			description: 'Priority for selection when multiple placements match (1 = highest)',
			defaultValue: 1,
			isRequired: false,
		},
		{
			type: 'Number',
			name: 'maxInsertsPerPage',
			description: 'Maximum number of inserts per page view (empty = use global setting)',
			defaultValue: null,
			isRequired: false,
			props: { between: { low: MaxInsertsPerPage.min, high: MaxInsertsPerPage.max } },
		},
		{
			type: 'Number',
			name: 'layoutExtensionPercent',
			description: 'Max allowed layout growth as % of largest ad size (0 = no growth allowed)',
			defaultValue: 10,
			isRequired: false,
			props: { between: { low: 0, high: 100 } },
		},
		{
			type: 'String',
			name: 'platforms',
			description: `Platforms`,
			hasOptions: true,
			multiSelect: true,
			defaultValue: null,
			omitWhenDefault: true,
			options: [
				...Object.entries(PLATFORMS).map(([label, value]) => ({
					name: value,
					label: `${label}`,
				})),
			],

		},
		{
			type: 'HTML',
			name: 'insertTemplate',
			description: 'HTML Template for insertion',
			omitWhenDefault: true,
			defaultValue: DYNAMIC_INSERT_DEFAULT_TEMPLATE,
		},
		{
			type: 'Array',
			description: `Target elements to insert at`,
			name: 'targets',
			fields: [
				...DYNAMIC_INSERT_TARGET_DEFAULTS('target'),
			],
		},
		{
			type: 'Array',
			name: 'spacingContainers',
			description: 'Shared spacing container',
			fields: [
				{
					type: 'String',
					name: 'selector',
					description: 'CSS selector',
				},
			],
		},
		{
			type: 'Array',
			description: `Restriction selectors`,
			name: 'restrictedSelectors',
			fields: [
				...DYNAMIC_INSERT_TARGET_DEFAULTS('restriction'),
			],
		},
		{
			type: 'Array',
			description: `URL Blocklist`,
			name: 'urlBlocklist',
			fields: [
				{
					type: 'String',
					name: 'url',
					description: 'Blocked URL',
				},
			],
		},
		{
			type: 'Boolean',
			name: 'urlBlocklistInversed',
			description: 'Inverse blocklist (only allow listed URLs)',
			defaultValue: false,
		},
	],
};

module.exports = { FieldSettings, DEFAULT_TARGET_FIELD_NAMES };
